import cognitoConfig, {
  getAuthorizeUrl,
  getLogoutUri,
  getLogoutUrl,
  getRedirectUri,
  getTokenUrl,
} from '../config/cognito';

const STORAGE = {
  ACCESS_TOKEN: 'cognito_access_token',
  REFRESH_TOKEN: 'cognito_refresh_token',
  ID_TOKEN: 'cognito_id_token',
  EXPIRES_AT: 'cognito_expires_at',
  CODE_VERIFIER: 'cognito_code_verifier',
  OAUTH_STATE: 'cognito_oauth_state',
  REDIRECT_URI: 'cognito_redirect_uri',
};

const REFRESH_BUFFER_MS = 60 * 1000;

const base64UrlEncode = (buffer) => {
  const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : buffer;
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
};

const generateCodeChallenge = async (codeVerifier) => {
  const data = new TextEncoder().encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
};

const generateState = () => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
};

const parseJwt = (token) => {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const saveTokens = (tokenResponse) => {
  sessionStorage.setItem(STORAGE.ACCESS_TOKEN, tokenResponse.access_token);
  if (tokenResponse.refresh_token) {
    sessionStorage.setItem(STORAGE.REFRESH_TOKEN, tokenResponse.refresh_token);
  }
  if (tokenResponse.id_token) {
    sessionStorage.setItem(STORAGE.ID_TOKEN, tokenResponse.id_token);
  }
  const expiresAt = Date.now() + tokenResponse.expires_in * 1000;
  sessionStorage.setItem(STORAGE.EXPIRES_AT, expiresAt.toString());
};

const clearTokens = () => {
  Object.values(STORAGE).forEach((key) => sessionStorage.removeItem(key));
};

const cleanOAuthParamsFromUrl = () => {
  window.history.replaceState({}, document.title, '/');
};

const exchangeCodeForTokens = async (code, codeVerifier, redirectUri) => {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cognitoConfig.clientId,
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  });

  const response = await fetch(getTokenUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Token exchange failed: ${errorText}`);
  }

  return response.json();
};

const refreshAccessToken = async () => {
  const refreshToken = sessionStorage.getItem(STORAGE.REFRESH_TOKEN);
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: cognitoConfig.clientId,
    refresh_token: refreshToken,
  });

  const response = await fetch(getTokenUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    throw new Error('Token refresh failed');
  }

  const tokenResponse = await response.json();
  saveTokens({ ...tokenResponse, refresh_token: tokenResponse.refresh_token || refreshToken });
  return tokenResponse;
};

const handleOAuthCallback = async () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');

  if (error) {
    console.error('OAuth error:', error, params.get('error_description'));
    cleanOAuthParamsFromUrl();
    return;
  }

  if (!code) return;

  const savedState = sessionStorage.getItem(STORAGE.OAUTH_STATE);
  if (!state || state !== savedState) {
    console.error('Invalid OAuth state');
    clearTokens();
    cleanOAuthParamsFromUrl();
    return;
  }

  const codeVerifier = sessionStorage.getItem(STORAGE.CODE_VERIFIER);
  if (!codeVerifier) {
    console.error('Missing PKCE code verifier');
    cleanOAuthParamsFromUrl();
    return;
  }

  const redirectUri = sessionStorage.getItem(STORAGE.REDIRECT_URI) || getRedirectUri();
  const tokens = await exchangeCodeForTokens(code, codeVerifier, redirectUri);
  saveTokens(tokens);

  sessionStorage.removeItem(STORAGE.CODE_VERIFIER);
  sessionStorage.removeItem(STORAGE.OAUTH_STATE);
  sessionStorage.removeItem(STORAGE.REDIRECT_URI);
  cleanOAuthParamsFromUrl();
};

const needsRefresh = () => {
  const expiresAt = sessionStorage.getItem(STORAGE.EXPIRES_AT);
  if (!expiresAt) return false;
  return Date.now() >= parseInt(expiresAt, 10) - REFRESH_BUFFER_MS;
};

const getTokenParsed = () => parseJwt(sessionStorage.getItem(STORAGE.ID_TOKEN));

/**
 * Initializes Cognito auth: handles OAuth callback if present, then renders the app.
 */
const initCognito = async (onReadyCallback) => {
  try {
    await handleOAuthCallback();
  } catch (error) {
    console.error('Cognito init failed:', error);
    clearTokens();
  }
  onReadyCallback();
};

const doLogin = async () => {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateState();

  const redirectUri = getRedirectUri();

  sessionStorage.setItem(STORAGE.CODE_VERIFIER, codeVerifier);
  sessionStorage.setItem(STORAGE.OAUTH_STATE, state);
  sessionStorage.setItem(STORAGE.REDIRECT_URI, redirectUri);

  const params = new URLSearchParams({
    client_id: cognitoConfig.clientId,
    response_type: 'code',
    scope: cognitoConfig.scopes,
    redirect_uri: redirectUri,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    state,
  });

  window.location.href = `${getAuthorizeUrl()}?${params.toString()}`;
};

const doLogout = () => {
  clearTokens();
  const params = new URLSearchParams({
    client_id: cognitoConfig.clientId,
    logout_uri: getLogoutUri(),
  });
  window.location.href = `${getLogoutUrl()}?${params.toString()}`;
};

const getToken = () => sessionStorage.getItem(STORAGE.ACCESS_TOKEN);

const isLoggedIn = () => {
  const token = getToken();
  const expiresAt = sessionStorage.getItem(STORAGE.EXPIRES_AT);
  if (!token || !expiresAt) return false;
  return Date.now() < parseInt(expiresAt, 10);
};

const updateToken = (successCallback) => {
  if (!needsRefresh()) {
    return Promise.resolve(successCallback());
  }
  return refreshAccessToken()
    .then(() => successCallback())
    .catch(() => {
      clearTokens();
      return doLogin();
    });
};

const getGivenName = () => {
  const claims = getTokenParsed();
  return claims?.given_name || claims?.nombre || '';
};

const getFamilyName = () => {
  const claims = getTokenParsed();
  return claims?.family_name || claims?.apellidos || '';
};

const getDisplayName = () => {
  const given = getGivenName();
  const family = getFamilyName();
  const full = `${given} ${family}`.trim();
  if (full) return full;
  const claims = getTokenParsed();
  return claims?.name || getUsername() || '';
};

const getUsername = () => {
  const claims = getTokenParsed();
  return claims?.email || claims?.['cognito:username'] || claims?.preferred_username;
};

const getCompanyName = () => {
  const claims = getTokenParsed();
  return claims?.codigoEmpresa || claims?.nombreEmpresa || '';
};

const hasRole = (roles) => {
  const claims = getTokenParsed();
  const groups = claims?.['cognito:groups'] || [];
  return roles.some((role) => groups.includes(role));
};

const getProfile = () => {
  const claims = getTokenParsed() || {};
  return {
    givenName: getGivenName(),
    familyName: getFamilyName(),
    displayName: getDisplayName(),
    email: getUsername() || '',
    company: getCompanyName() || '',
    username: claims['cognito:username'] || claims.preferred_username || '',
    groups: claims['cognito:groups'] || [],
    sub: claims.sub || '',
  };
};

const UserService = {
  initCognito,
  doLogin,
  doLogout,
  isLoggedIn,
  getToken,
  updateToken,
  getUsername,
  getGivenName,
  getFamilyName,
  getDisplayName,
  getCompanyName,
  hasRole,
  getProfile,
};

export default UserService;
